import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  ArrowLeft2,
  ArrowRight2,
  Cross,
} from "../CustomComponents/SwiftIcon/Icons";
import {
  calculateRemainingInstallmentsFromStrings,
  numberFormat,
  priceFormat,
} from "../../exports/UtilFunctions";
import MFSearch from "./MFSearch";
import ServerRequest from "../../exports/ServerRequest";
import {
  generateInstallmentsDropdownOptions,
  generatePurchaseDropdownOptions,
  getNextSipInstallmentDate,
  getSipDateOptions,
  getSipDates,
} from "../../exports/generatePurchaseDropdownOptions";
import CustomSelect from "../Body/CustomChartComponents/CustomSelect/CustomSelect";
import CustomSelectPortal from "../Body/CustomChartComponents/CustomSelect/CustomSelectPortal";
import CustomIconButton from "../Body/CustomChartComponents/CustomIconButton/CustomIconButton";
import ConfirmationBox from "./ConfirmationBox";
import { COLOR_VARS } from "../../exports/ColorVars";
import { getDeviceSize } from "../../exports/InteractiveUtils";
import Pulse from "../Loader/Pulse";
import { MFBuySellErrorhandling } from "./MFErrorHandling";
import { Alert } from "../Body/CustomChartComponents/CustomAlert/CustomAlert";
import ConfirmationTable from "./ConfirmationTable";

const warning_config = {
  Band: true,
  BandColor: "var(--main-blue)",
  BoxColor: "var(--white)",
  TextColor: "var(--text-color)",
  AutoClose: {
    Active: true,
    Line: true,
    LineColor: "var(--main-blue)",
    Time: 3,
  },
};

const DEVICE_SIZE = getDeviceSize();

/** ------------------------
 * Small, re-usable helpers
 * -----------------------*/
const getISIN = (obj) => obj?.isin || obj?.ISIN || "";
const getSchemeName = (obj) => obj?.scheme_name || obj?.Scheme_Name || "";
const getSchemeCode = (obj) => obj?.scheme_code || obj?.Scheme_Code || "";

const normalizeFromPortfolioOrSearch = (data) => ({
  scheme_name: getSchemeName(data),
  scheme_code: getSchemeCode(data),
  isin: getISIN(data),
  folio_no: data?.folio_no || "",
  ...data,
});

const hasAllData = (items) =>
  Array.isArray(items) &&
  items.length > 0 &&
  items.every((item) => item?.sipInfo && item?.current_nav !== undefined);

const buildDropdowns = (h) => {
  // Amounts
  const amountOptions = generatePurchaseDropdownOptions(
    parseInt(h?.sipInfo?.min_sip_amount ?? 0, 10),
    parseInt(h?.sipInfo?.multiple ?? 0, 10),
    parseInt(
      h?.sipInfo?.max_sip_amount_l1
        ? h?.sipInfo?.max_sip_amount_l1
        : (h?.sipInfo?.max_sip_amount ?? 0),
      10,
    ),
  ).map((val) => ({
    label: val === "ALL" ? "All Units" : val === "" ? "-" : priceFormat(val),
    value: val === "ALL" ? h?.investment_value : val === "" ? 0 : val,
  }));

  // Installments
  const instOptions = generateInstallmentsDropdownOptions(
    parseInt(h?.sipInfo?.min_installments ?? 0, 10),
    1,
    120,
  ).map((val) => ({
    label:
      val === "Until cancelled" ? "Until cancelled" : val === "" ? "-" : val,
    value:
      val === "Until cancelled"
        ? h?.sipInfo?.max_installments
        : val === ""
          ? 0
          : val,
  }));

  // Dates
  const rawDates = getSipDateOptions(
    h?.sipInfo?.sip_dates_l1
      ? h?.sipInfo?.sip_dates_l1
      : h?.sipInfo?.sip_dates || [],
  );
  const dateOptions = rawDates.map((val) => {
    if (val === "") return { label: "-", value: 0 };
    if (val === "Earliest") return { label: "Earliest", value: "Earliest" };
    return { label: val, value: val };
  });

  return { amountOptions, instOptions, dateOptions };
};

const getNextDates = ({ h, desirableDay }) => {
  const now = new Date();
  return getNextSipInstallmentDate({
    isin: h?.sipInfo?.isin,
    day: now.getDate(),
    month: now.getMonth() + 1,
    year: now.getFullYear(),
    desirableDay,
    sipDates: h?.sipInfo?.sip_dates_l1
      ? h?.sipInfo?.sip_dates_l1
      : h?.sipInfo?.sip_dates,
  });
};

/** ========================
 * MAIN COMPONENT: MFSIP
 * =======================*/
const MFSIP = ({ popupData, portfolioData = [], setPopup, customer }) => {
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("Loading mutual fund data...");
  const [opacityMsg, setOpacityMsg] = useState(true);

  const [transferData, setTransferData] = useState([]);
  const [currentStep, setCurrentStep] = useState("Process");
  const [confirmloading, setConfirmLoading] = useState(false);
  const [bankAccount, setBankAccount] = useState("");
  const [selectedTxnType, setSelectedTxnType] = useState("NETBANKING");
  const [vpa, setVpa] = useState("");

  const [clientAccounts, setClientAccounts] = useState([]);
  const [mandateDetails, setMandateDeails] = useState([]);
  const [sipMandateId, setSipMandateId] = useState("");
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmationType, setConfirmationType] = useState("");
  const [confirmationMessage, setConfirmmationMessage] =
    useState("Placing your order");
  const [purchaseMessage, setPurchaseMessage] = useState("");
  const [successData, setSuccessData] = useState([]);
  const [failedData, setFailedData] = useState([]);

  const fetchedNAVs = useRef(new Set());
  const fetchedISINs = useRef(new Set());
  const isSmallDevice = DEVICE_SIZE === "S";

  /** ------------------------
   * Initialize from popupData
   * -----------------------*/
  useEffect(() => {
    if (!popupData) return;
    const newItems = popupData?.isSingle
      ? [
          {
            ...popupData,
            source: "popup",
            uniqueId: Date.now() + Math.random(),
          },
        ]
      : Array.isArray(popupData)
        ? popupData.map((item) => ({
            ...item,
            source: "popup",
            uniqueId: Date.now() + Math.random(),
          }))
        : [];

    if (!newItems.length) return;

    setTransferData(newItems);
  }, [popupData]);

  useEffect(() => {
    if (!confirmloading) return;

    const loadingMessages = ["Loading your transaction...", "Almost there..."];
    let index = 0;
    setConfirmmationMessage(loadingMessages[index]);
    setOpacityMsg(true);

    const loadingMessageInterval = setInterval(() => {
      setOpacityMsg(false);
      setTimeout(() => {
        index = (index + 1) % loadingMessages.length;
        setConfirmmationMessage(loadingMessages[index]);
        setOpacityMsg(true);
      }, 200);
    }, 3000);

    return () => clearInterval(loadingMessageInterval);
  }, [loading]);

  /** --------------------------------
   * Fetch SIP purchase info per ISIN
   * -------------------------------*/
  const fetchPurchaseInfo = useCallback(async (isin) => {
    try {
      const res = await ServerRequest({
        url: `/mf/sipamount/${isin}`,
        method: "GET",
      });
      if (res?.data) {
        setTransferData((prev) =>
          prev.map((item) =>
            getISIN(item) === isin ? { ...item, sipInfo: res.data } : item,
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching purchase info for ISIN:", isin, error);
    }
  }, []);

  useEffect(() => {
    transferData.forEach((item) => {
      const isin = getISIN(item);
      if (isin && !fetchedISINs.current.has(isin)) {
        fetchedISINs.current.add(isin);
        fetchPurchaseInfo(isin);
      }
    });
  }, [transferData, fetchPurchaseInfo]);

  /** -------------------------
   * Fetch latest NAV per ISIN
   * ------------------------*/
  const fetchCurrentNAV = useCallback(async (isin) => {
    try {
      const res = await ServerRequest({
        url: `/mf/latestnav/${isin}`,
        method: "get",
      });
      const nav = res?.result?.latest_nav;
      if (res) {
        setTransferData((prev) =>
          prev.map((item) =>
            getISIN(item) === isin ? { ...item, current_nav: nav } : item,
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching current NAV for ISIN:", isin, error);
    }
  }, []);

  useEffect(() => {
    transferData.forEach((item) => {
      const isin = getISIN(item);
      if (
        isin &&
        (item.current_nav === undefined || item.current_nav === null) &&
        !fetchedNAVs.current.has(isin)
      ) {
        fetchedNAVs.current.add(isin);
        fetchCurrentNAV(isin);
      }
    });
  }, [transferData, fetchCurrentNAV]);

  /** --------------------------
   * Loading state orchestration
   * -------------------------*/
  useEffect(() => {
    if (popupData?.isNewFund) {
      setLoading(false);
      return;
    }
    setLoading(!hasAllData(transferData));
  }, [transferData, popupData?.isNewFund]);

  useEffect(() => {
    console.log(mandateDetails);
  }, [mandateDetails]);

  useEffect(() => {
    if (!loading) return;
    const messages = ["Loading mutual fund data...", "Almost there..."];
    let idx = 0;

    setLoadingMsg(messages[idx]);
    setOpacityMsg(true);

    const interval = setInterval(() => {
      setOpacityMsg(false);
      setTimeout(() => {
        idx = (idx + 1) % messages.length;
        setLoadingMsg(messages[idx]);
        setOpacityMsg(true);
      }, 200);
    }, 3000);

    return () => clearInterval(interval);
  }, [loading]);

  /** --------------------------
   * Totals (memoized)
   * -------------------------*/
  const { totalCurrentValue, totalTransactionValue } = useMemo(() => {
    let invest = 0;
    let txn = 0;
    transferData.forEach((val) => {
      invest += Number(val?.investment_value) || 0;
      txn += Number(val?.selectedAmount) || 0;
    });
    return {
      totalCurrentValue: Number(invest.toFixed(2)),
      totalTransactionValue: txn,
    };
  }, [transferData]);

  /** --------------------------
   * Event Handlers
   * -------------------------*/
  const removeRow = useCallback((index) => {
    setTransferData((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSearchSelect = useCallback(
    (symbol) => {
      if (!symbol) return;
      const normalized = normalizeFromPortfolioOrSearch(symbol);
      const portfolioMatch = (portfolioData || []).find(
        (p) => (p.isin || p.ISIN) === normalized.isin,
      );
      const dataToPush = {
        ...(portfolioMatch || symbol),
        source: "search",
        uniqueId: Date.now() + Math.random(),
      };
      setTransferData((prev) => {
        const exists = prev.some(
          (item) =>
            (item.isin || item.ISIN) === (dataToPush.isin || dataToPush.ISIN),
        );
        return exists ? prev : [...prev, dataToPush];
      });
    },
    [portfolioData],
  );

  const handleAmountChange = useCallback((value, uniqueId) => {
    setTransferData((prev) =>
      prev.map((item) =>
        item.uniqueId === uniqueId ? { ...item, selectedAmount: value } : item,
      ),
    );
  }, []);

  const handleInstChange = useCallback((value, uniqueId) => {
    setTransferData((prev) =>
      prev.map((item) => {
        if (item.uniqueId !== uniqueId) return item;

        // Convert marker back into API value
        const actualValue =
          value === "UNTIL_CANCELLED"
            ? (item?.sipInfo?.max_installments ?? 9999)
            : value;

        return { ...item, selectedInstallments: actualValue };
      }),
    );
  }, []);

  const handleInstDateChange = useCallback((value, uniqueId) => {
    setTransferData((prev) =>
      prev.map((item) => {
        if (item.uniqueId !== uniqueId) return item;
        if (item.selectedInstallmentDate === value) return item;

        const nextInstDate = getNextDates({ h: item, desirableDay: value });
        return {
          ...item,
          selectedInstallmentDate: value,
          start_date: nextInstDate?.current_date,
          next_date: nextInstDate?.desirable_installment,
        };
      }),
    );
  }, []);

  /** --------------------------
   * Step: Select Mandate
   * -------------------------*/
  const handleProcess = useCallback(async () => {
    try {
      const [accRes, mandateRes] = await Promise.all([
        ServerRequest({
          url: `/nse/get-client-account?account_code=${customer?.account_code}`,
          method: "get",
        }),
        ServerRequest({
          url: `/nse/get-mandate-data?account_code=${customer?.account_code}`,
          method: "get",
        }),
      ]);

      const accList = accRes?.data?.report_data || [];
      const mandateList = mandateRes?.data?.report_data || [];

      console.log("mandateList", mandateList);

      const isAPPROVED = mandateList.some((m) => m.status === "APPROVED");

      if (isAPPROVED) {
        setClientAccounts(accList);
        setMandateDeails(mandateList);
        setCurrentStep("Confirmation");
      } else {
        Alert({
          TitleText: "Warning",
          Message: "Some of your mandates are under process",
          ...warning_config,
        });
        return;
      }
    } catch (error) {
      console.error("Error fetching client account/mandates", error);
    }
  }, [customer?.account_code]);

  const formattedMandateOptions = useMemo(() => {
    return (mandateDetails || [])
      .filter((m) => m?.status === "APPROVED")
      .map((bank) => ({
        label: (
          <div className="bank-option">
            <div className="bank-name">
              {bank?.bankName}, Acc. No.: {bank?.bankAccountNumber}
            </div>
            <div className="bank-account">
              Amt.: {bank?.amount}, UMRN: {bank?.umrnNo}
            </div>
          </div>
        ),
        value: bank?.mandateId,
      }));
  }, [mandateDetails]);

  useEffect(() => {
    if (!sipMandateId && formattedMandateOptions.length > 0) {
      const firstMandate = mandateDetails?.find(
        (m) =>
          String(m?.mandateId) === String(formattedMandateOptions[0].value),
      );

      if (firstMandate) {
        setSipMandateId(firstMandate.mandateId);
        setBankAccount(firstMandate.bankAccountNumber);
      }
    }
  }, [sipMandateId, formattedMandateOptions, mandateDetails]);

  useEffect(() => {
    console.log(bankAccount);
  }, [bankAccount]);

  useEffect(() => {
    console.log(mandateDetails);
  }, [mandateDetails]);

  /** --------------------------
   * Confirm (Place Registration)
   * -------------------------*/

  const handleConfirmClick = () => {
    if (selectedTxnType === "UPI" && (!vpa || vpa.trim() === "")) {
      Alert({
        TitleText: "Warning",
        Message: "Please enter a valid UPI ID.",
        ...warning_config,
      });
      return; // stop here only for UPI without VPA
    }
    handleConfirm(); // proceed for all other cases
  };
  const handleConfirm = useCallback(async () => {
    setConfirmLoading(true);
    const transactionTemplate = {
      amc_code: "",
      sch_code: "",
      client_code: "",
      internal_ref_no: "",
      trans_mode: "P",
      dp_txn_mode: "P",
      start_date: "",
      frequency_type: "MONTHLY",
      frequency_allowed: "1",
      installment_amount: "",
      xsip_mandate_id: "",
      convenience_fee: "",
      status: "1",
      member_code: "",
      folio_no: "",
      sip_remarks: "",
      installment_no: "",
      isip_mandate_id: "",
      sub_broker_code: "",
      euin_number: "",
      euin_declaration: "Y",
      dpc_flag: "Y",
      first_order_today: "Y",
      sub_broker_arn: "",
      end_date: "",
      primary_holder_mobile: "",
      primary_holder_email: "",
      step_up_required: "N",
      step_up_start_date: "",
      step_up_end_date: "",
      step_up_frequency: "",
      step_up_amount: "",
      filler_1: "",
      filler_2: "",
      filler_3: "",
      filler_4: "",
      filler_5: "",
      reg_id: "",
      reg_status: "",
      reg_remark: "",
    };

    try {
      const reference = await ServerRequest({
        url: "/mf/orderreferenceno",
        method: "get",
      });
      const orderRefNumber = reference?.data || "";

      const newTranferdata = transferData.filter(
        (item) => parseFloat(item.selectedAmount) > 0,
      );
      setTransferData(newTranferdata);

      // const endDateStr =
      const reg_data = await Promise.all(
        newTranferdata.map(async (item) => {
          const sipDates = getSipDates(item.sipInfo.sip_dates);
          const nextSipDateStr = item.next_date;
          const selectedMandate = mandateDetails.find(
            (item) => item.mandateId === sipMandateId,
          );
          const endDateStr = selectedMandate.endDate;

          const mandateInstallments = calculateRemainingInstallmentsFromStrings(
            { nextSipDateStr, endDateStr, sipDates },
          );
          console.log(
            mandateInstallments,
            nextSipDateStr,
            sipDates,
            endDateStr,
          );

          const amount = parseFloat(item?.selectedAmount || 0);
          const threshold = parseFloat(item?.sipInfo?.min_sip_amount_l1 || 0);
          return {
            ...transactionTemplate,
            amc_code: item?.sipInfo?.amc_code ?? "",
            folio_no: item?.folio_no || "",
            sch_code: item?.sipInfo?.scheme_code,
            client_code: clientAccounts?.[0]?.client_code || "",
            member_code: clientAccounts?.[0]?.member_code || "",
            xsip_mandate_id: sipMandateId || "",
            installment_amount: String(item?.selectedAmount ?? ""),
            installment_no:
              mandateInstallments < item?.selectedInstallments
                ? mandateInstallments
                : String(item?.selectedInstallments ?? ""),
            start_date: item?.next_date,
            internal_ref_no: orderRefNumber,
          };
        }),
      );

      const res = await ServerRequest({
        url: "/nse/sip-registration",
        method: "post",
        data: {
          reg_data: reg_data,
          transferData: newTranferdata,
          clientAccounts: clientAccounts,
          bankAccount: bankAccount,
        },
      });

      console.log(res);

      const results = res?.data?.reg_data || [];
      const success = results.filter((r) => r.reg_status === "REG_SUCCESS");
      const fail = results.filter((r) => r.reg_status === "REG_FAILED");

      setSuccessData(success);
      setFailedData(fail);

      console.log(success, fail);

      if (success.length > 0) {
        setPurchaseMessage("Please check your email for transaction");
      }
      setCurrentStep("FinalConfirmation");
    } catch (err) {
      console.error("SIP registration failed:", err);
      setConfirmMessage("Registration failed due to a server error.");
      setConfirmationType("fail");
      setCurrentStep("FinalConfirmation");
    } finally {
      setConfirmLoading(false);
    }
  }, [clientAccounts, sipMandateId, transferData]);

  const getTransactionTypeOptions = (amount) => {
    if (amount < 100000) {
      return [
        { label: "UPI", value: "UPI" },
        { label: "Net Banking", value: "NETBANKING" },
        { label: "NEFT/ RTGS", value: "NEFT" },
      ];
    } else if (amount < 1000000) {
      return [
        { label: "Net Banking", value: "NETBANKING" },
        { label: "NEFT/ RTGS", value: "NEFT" },
      ];
    } else {
      return [{ label: "NEFT/ RTGS", value: "NEFT" }];
    }
  };

  const transactionTypeOptions = getTransactionTypeOptions(
    totalTransactionValue,
  );
  const defaultTransactionType = transactionTypeOptions[0]?.value;

  /** --------------------------
   * Render
   * -------------------------*/
  if (loading || confirmloading) {
    return (
      <div className="app__login__screen">
        <div className="app__login__process">
          <Pulse />
          {/* <p className={opacityMsg ? 'fadeIn' : 'fadeOut'}>{confirmloading ? confirmationMessage : loadingMsg}</p> */}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="swift-mf-buysell-popup-header">
        <div
          className={`swift-mf-popup-btn ${
            Array.isArray(popupData)
              ? popupData?.[0]?.type?.toLowerCase()
              : popupData?.type?.toLowerCase() || ""
          }`}
        >
          Monthly{" "}
          {Array.isArray(popupData) ? popupData?.[0]?.type : popupData?.type}
        </div>

        {currentStep === "Process" && !popupData?.isSingle && (
          <MFSearch handleSelect={handleSearchSelect} />
        )}
      </div>

      <div className="swift-mf-popup-body">
        <div
          className={`mf-BuySell-container ${currentStep === "Confirmation" ? "Confirmation" : ""}`}
        >
          {currentStep === "Process" && (
            <>
              {isSmallDevice ? (
                <>
                  <BuySellCardContainer
                    transferData={transferData}
                    removeRow={removeRow}
                    handleAmountChange={handleAmountChange}
                    handleInstChange={handleInstChange}
                    handleInstDateChange={handleInstDateChange}
                    setTransferData={setTransferData}
                    CustomSelect={CustomSelect}
                  />
                  {!popupData?.isSingle && (
                    <BuySellConfig
                      current_value={totalCurrentValue}
                      transaction_value={totalTransactionValue}
                    />
                  )}
                </>
              ) : (
                <>
                  <table>
                    <thead>
                      <tr>
                        <td className="scheme-name">Scheme Name</td>
                        <td className="isin">Scheme Code</td>
                        <td className="isin">ISIN</td>
                        <td className="folio">Folio No</td>
                        <td className="inv-value">Current Value</td>
                        <td>Current Nav</td>
                        <td className="post-transaction">Transaction Value</td>
                        <td className="post-transaction">No. Of Inst.</td>
                        <td className="post-transaction">Inst. Date</td>
                        <td className="post-transaction">Next Inst. Date</td>
                        <td className="cross"></td>
                      </tr>
                    </thead>
                    <tbody>
                      {transferData.map((h, idx) => {
                        if (!h?.sipInfo) return null;
                        const { amountOptions, instOptions, dateOptions } =
                          buildDropdowns(h, mandateDetails);

                        const chosenSchemeCode =
                          parseFloat(h?.selectedAmount || 0) >
                          parseFloat(h?.sipInfo?.min_sip_amount_l1 || 0)
                            ? h?.sipInfo?.scheme_code_l1
                            : h?.sipInfo?.scheme_code || getSchemeCode(h) || "";

                        return (
                          <tr key={getISIN(h) || idx}>
                            <td className="body-scheme-name">
                              {getSchemeName(h)}
                            </td>
                            <td className="body-category">
                              {chosenSchemeCode}
                            </td>
                            <td>{getISIN(h)}</td>
                            <td>{h?.folio_no || ""}</td>
                            <td>{priceFormat(h?.investment_value || 0)}</td>
                            <td>{priceFormat(h?.current_nav || 0)}</td>
                            <td className="custom-text-td">
                              {h?.sipInfo ? (
                                <div className="bysell-custom-select-container">
                                  <CustomSelectPortal
                                    value={h?.selectedAmount}
                                    defaultIndex={
                                      h?.selectedAmount
                                        ? amountOptions.findIndex(
                                            (opt) =>
                                              String(opt.value) ===
                                              String(h?.selectedAmount),
                                          )
                                        : -1
                                    }
                                    options={amountOptions.map(
                                      (opt) => opt.label,
                                    )}
                                    width={180}
                                    height={50}
                                    placeholder="-"
                                    onTypeChange={(label) => {
                                      const matched = amountOptions.find(
                                        (opt) => opt.label === label,
                                      );
                                      const amount = matched?.value ?? 0;

                                      handleAmountChange(amount, h.uniqueId);

                                      // ✅ Only set defaults if amount > 0
                                      if (amount > 0) {
                                        const untilCancelled = instOptions.find(
                                          (opt) =>
                                            opt.label === "Until cancelled",
                                        );
                                        if (untilCancelled)
                                          handleInstChange(
                                            untilCancelled.value,
                                            h.uniqueId,
                                          );

                                        const earliest = dateOptions.find(
                                          (opt) => opt.label === "Earliest",
                                        );
                                        if (earliest)
                                          handleInstDateChange(
                                            earliest.value,
                                            h.uniqueId,
                                          );
                                      } else {
                                        handleInstChange(0, h.uniqueId);
                                        handleInstDateChange(0, h.uniqueId);
                                      }
                                    }}
                                  />
                                </div>
                              ) : (
                                <span className="not_allowed">Not Allowed</span>
                              )}
                            </td>
                            <td className="custom-text-td">
                              {h?.sipInfo ? (
                                <div className="bysell-custom-select-container">
                                  <CustomSelectPortal
                                    value={h?.selectedInstallments}
                                    defaultIndex={
                                      h?.selectedInstallments
                                        ? instOptions.findIndex(
                                            (opt) =>
                                              String(opt.value) ===
                                              String(h?.selectedInstallments),
                                          )
                                        : -1
                                    }
                                    options={instOptions.map(
                                      (opt) => opt.label,
                                    )}
                                    width={180}
                                    height={50}
                                    placeholder="-"
                                    onTypeChange={(label) => {
                                      const matched = instOptions.find(
                                        (opt) => opt.label === label,
                                      );
                                      handleInstChange(
                                        matched?.value ?? 0,
                                        h.uniqueId,
                                      );
                                    }}
                                  />
                                </div>
                              ) : (
                                <span className="not_allowed">Not Allowed</span>
                              )}
                            </td>
                            <td className="custom-text-td">
                              {h?.sipInfo ? (
                                <div className="bysell-custom-select-container">
                                  <CustomSelectPortal
                                    value={h?.selectedInstallmentDate}
                                    defaultIndex={
                                      h?.selectedInstallmentDate
                                        ? dateOptions.findIndex(
                                            (opt) =>
                                              String(opt.value) ===
                                              String(
                                                h?.selectedInstallmentDate,
                                              ),
                                          )
                                        : -1
                                    }
                                    options={dateOptions.map(
                                      (opt) => opt.label,
                                    )}
                                    width={180}
                                    height={50}
                                    placeholder="-"
                                    onTypeChange={(label) => {
                                      const matched = dateOptions.find(
                                        (opt) => opt.label === label,
                                      );
                                      handleInstDateChange(
                                        matched?.value ?? 0,
                                        h.uniqueId,
                                      );
                                    }}
                                  />
                                </div>
                              ) : (
                                <span className="not_allowed">Not Allowed</span>
                              )}
                            </td>
                            <td>
                              {h?.next_date && h.selectedAmount > 0
                                ? h?.next_date
                                : "-"}
                            </td>
                            {h?.source === "search" && (
                              <td
                                onClick={() => removeRow(idx)}
                                style={{ cursor: "pointer" }}
                              >
                                <Cross color={COLOR_VARS["SWIFT_COLOR4"]} />
                              </td>
                            )}
                          </tr>
                        );
                      })}

                      {transferData.some((val) => val?.isSingle === false) && (
                        <tr className="total-row">
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td>{priceFormat(totalCurrentValue)}</td>
                          <td></td>
                          <td>{priceFormat(totalTransactionValue)}</td>
                          <td></td>
                          <td></td>
                          <td></td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}

          {currentStep === "Confirmation" && (
            <div className="mf_buysell_bank_selection_container">
              <div style={{ padding: "10px 0" }}>
                <p className="mf_buysell_bank_selection">Mandate Details</p>
                <div style={{ marginLeft: "10px" }}>
                  <CustomSelect
                    options={formattedMandateOptions.map((opt) => opt.label)}
                    title="Select Mandate Details"
                    defaultIndex={
                      sipMandateId
                        ? formattedMandateOptions.findIndex(
                            (opt) => String(opt.value) === String(sipMandateId),
                          )
                        : 0 // 👈 fallback to the first mandate
                    }
                    value={
                      formattedMandateOptions.find(
                        (opt) => opt.value === sipMandateId,
                      )?.label || formattedMandateOptions[0]?.label
                    }
                    onTypeChange={(label) => {
                      const selected = formattedMandateOptions.find(
                        (opt) => opt.label === label,
                      );
                      const selectedMandate = (mandateDetails || []).find(
                        (m) => String(m?.mandateId) === String(selected?.value),
                      );

                      console.log(selectedMandate);

                      setSipMandateId(selectedMandate?.mandateId || "");
                      setBankAccount(selectedMandate?.bankAccountNumber);
                    }}
                    width={260}
                    height={44}
                  />
                </div>
              </div>
            </div>
          )}

          <>
            {currentStep === "FinalConfirmation" && (
              <>
                {successData.length > 0 || failedData.length > 0 ? (
                  <ConfirmationTable
                    successData={successData}
                    failedData={failedData}
                    purchaseMessage={purchaseMessage}
                    transferData={transferData}
                  />
                ) : (
                  <ConfirmationBox
                    msg={
                      confirmMessage === "Success"
                        ? "Your order has been successfully confirmed"
                        : "Looks like your transaction/order has failed."
                    }
                    addMsg={purchaseMessage}
                    confirmationType={confirmMessage}
                  />
                )}
              </>
            )}
          </>
        </div>

        <div className="step-button mf-swift-buysell-confirm-button">
          {currentStep === "Process" && (
            <>
              <button onClick={() => setPopup(false)} className="btn-outline">
                Back
              </button>
              <CustomIconButton
                title="Select Mandate"
                onClick={handleProcess}
                disabled={
                  !transferData.some((item) => Number(item?.selectedAmount) > 0)
                }
                icon={null}
              />
            </>
          )}

          {currentStep === "Confirmation" && (
            <>
              <button
                onClick={() => setCurrentStep("Process")}
                className="btn-outline"
              >
                Back
              </button>
              <CustomIconButton
                title={`Confirm`}
                onClick={handleConfirmClick}
                disabled={!sipMandateId}
                icon={null}
              />
            </>
          )}
          {currentStep === "FinalConfirmation" && (
            <CustomIconButton
              title={`Close`}
              onClick={() => setPopup(false)}
              icon={<Cross size={20} color={COLOR_VARS["SWIFT_COLOR6"]} />}
            />
          )}
        </div>
      </div>
    </>
  );
};

/** ========================
 * Subcomponents (same file)
 * =======================*/
const BuySellCardContainer = ({
  transferData,
  removeRow,
  handleAmountChange,
  CustomSelect,
  handleInstDateChange,
  setTransferData,
  handleInstChange,
}) => {
  return (
    <>
      {transferData.map((h, idx) => {
        if (!h?.sipInfo) return null;

        const { amountOptions, instOptions, dateOptions } = buildDropdowns(h);

        const chosenSchemeCode =
          parseFloat(h?.selectedAmount || 0) >
          parseFloat(h?.sipInfo?.min_sip_amount_l1 || 0)
            ? h?.sipInfo?.scheme_code_l1
            : h?.sipInfo?.scheme_code || getSchemeCode(h) || "";

        return (
          <div className="mf-watchlist-card" key={getISIN(h) || idx}>
            <div className="mfwc-info">
              <div className="isin-price">
                <p className="isin-type">{getISIN(h)}</p>
                <p className="price-change">
                  {priceFormat(h?.current_nav || 0)}
                </p>
              </div>
              <p className="name">{getSchemeName(h)}</p>
              <p className="category">{chosenSchemeCode}</p>
            </div>

            <div className="mfwc-config">
              <div className="mfwc-config-row">
                <p>
                  Current Value{" "}
                  <span>{priceFormat(h?.investment_value || 0)}</span>
                </p>
                <p>
                  Transaction Value
                  {h?.sipInfo ? (
                    <div className="bysell-custom-select-container">
                      <CustomSelect
                        value={h?.selectedAmount}
                        defaultIndex={
                          h?.selectedAmount
                            ? amountOptions.findIndex(
                                (opt) =>
                                  String(opt.value) ===
                                  String(h?.selectedAmount),
                              )
                            : -1
                        }
                        options={amountOptions.map((opt) => opt.label)}
                        width={180}
                        height={50}
                        placeholder="-"
                        onTypeChange={(label) => {
                          const matched = amountOptions.find(
                            (opt) => opt.label === label,
                          );
                          const amount = matched?.value ?? 0;

                          handleAmountChange(amount, h.uniqueId);

                          // ✅ Only set defaults if amount > 0
                          if (amount > 0) {
                            const untilCancelled = instOptions.find(
                              (opt) => opt.label === "Until cancelled",
                            );
                            if (untilCancelled)
                              handleInstChange(
                                untilCancelled.value,
                                h.uniqueId,
                              );

                            const earliest = dateOptions.find(
                              (opt) => opt.label === "Earliest",
                            );
                            if (earliest)
                              handleInstDateChange(earliest.value, h.uniqueId);
                          } else {
                            handleInstChange(0, h.uniqueId);
                            handleInstDateChange(0, h.uniqueId);
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <span className="not_allowed">Not Allowed</span>
                  )}
                </p>
              </div>

              <div className="mfwc-config-row">
                <p>
                  No. Of Inst.
                  <span>
                    {h?.sipInfo ? (
                      <div className="bysell-custom-select-container">
                        <CustomSelect
                          value={h?.selectedInstallments}
                          defaultIndex={
                            h?.selectedInstallments
                              ? instOptions.findIndex(
                                  (opt) =>
                                    String(opt.value) ===
                                    String(h?.selectedInstallments),
                                )
                              : -1
                          }
                          options={instOptions.map((opt) => opt.label)}
                          width={180}
                          height={50}
                          placeholder="-"
                          onTypeChange={(label) => {
                            const matched = instOptions.find(
                              (opt) => opt.label === label,
                            );
                            handleInstChange(matched?.value ?? 0, h.uniqueId);
                          }}
                        />
                      </div>
                    ) : (
                      <span className="not_allowed">Not Allowed</span>
                    )}
                  </span>
                </p>

                <p>
                  Inst. Date
                  <span>
                    {h?.sipInfo ? (
                      <div className="bysell-custom-select-container">
                        <CustomSelect
                          value={h?.selectedInstallmentDate}
                          defaultIndex={
                            h?.selectedInstallmentDate
                              ? dateOptions.findIndex(
                                  (opt) =>
                                    String(opt.value) ===
                                    String(h?.selectedInstallmentDate),
                                )
                              : -1
                          }
                          options={dateOptions.map((opt) => opt.label)}
                          width={180}
                          height={50}
                          placeholder="-"
                          onTypeChange={(label) => {
                            const matched = dateOptions.find(
                              (opt) => opt.label === label,
                            );
                            handleInstDateChange(
                              matched?.value ?? 0,
                              h.uniqueId,
                            );
                          }}
                        />
                      </div>
                    ) : (
                      <span className="not_allowed">Not Allowed</span>
                    )}
                  </span>
                </p>
              </div>

              <div className="mfwc-config-row">
                <p>
                  Next Inst. Date{" "}
                  <span>{h?.next_date ? h?.next_date : "-"}</span>
                </p>
                <p>
                  {h?.source === "search" && (
                    <span
                      onClick={() => removeRow(idx)}
                      style={{ cursor: "pointer" }}
                    >
                      <p>Remove</p>
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

function BuySellConfig({ current_value, transaction_value }) {
  return (
    <div className="portfolio-config">
      <div className="portfolio-rows">
        <div className="portfolio-info-box">
          <span>Current Value</span>
          <p>Rs. {numberFormat(current_value)}</p>
        </div>
        <div className="portfolio-info-box">
          <span>Transaction Value</span>
          <p>Rs. {numberFormat(transaction_value)}</p>
        </div>
      </div>
    </div>
  );
}

export default MFSIP;
